import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Users, Search } from "lucide-react";
import { todayISO, addDaysISO } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

export default function BookingWidget({ compact = false }) {
  const nav = useNavigate();
  const { t } = useI18n();
  const [checkIn, setCheckIn] = useState(todayISO());
  const [checkOut, setCheckOut] = useState(addDaysISO(todayISO(), 2));
  const [guests, setGuests] = useState(2);

  function submit(e) {
    e.preventDefault();
    const q = new URLSearchParams({ check_in: checkIn, check_out: checkOut, guests: String(guests) });
    nav(`/rooms?${q.toString()}`);
  }

  return (
    <form
      onSubmit={submit}
      data-testid="booking-widget"
      className={`bg-white shadow-2xl rounded-sm border border-brand-border ${
        compact ? "p-3" : "p-3 md:p-4"
      } grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-0 items-stretch`}
    >
      <Field
        label={t("widget.checkin")}
        icon={<Calendar size={15} />}
        className="md:col-span-3 md:border-r border-brand-border"
      >
        <input
          data-testid="widget-checkin"
          type="date"
          value={checkIn}
          min={todayISO()}
          onChange={(e) => {
            setCheckIn(e.target.value);
            if (e.target.value >= checkOut) setCheckOut(addDaysISO(e.target.value, 1));
          }}
          className="w-full bg-transparent text-brand-text font-medium outline-none text-sm"
        />
      </Field>
      <Field
        label={t("widget.checkout")}
        icon={<Calendar size={15} />}
        className="md:col-span-3 md:border-r border-brand-border"
      >
        <input
          data-testid="widget-checkout"
          type="date"
          value={checkOut}
          min={addDaysISO(checkIn, 1)}
          onChange={(e) => setCheckOut(e.target.value)}
          className="w-full bg-transparent text-brand-text font-medium outline-none text-sm"
        />
      </Field>
      <Field
        label={t("widget.guests")}
        icon={<Users size={15} />}
        className="md:col-span-3 md:border-r border-brand-border"
      >
        <select
          data-testid="widget-guests"
          value={guests}
          onChange={(e) => setGuests(Number(e.target.value))}
          className="w-full bg-transparent text-brand-text font-medium outline-none text-sm cursor-pointer"
        >
          {[1, 2, 3, 4, 5, 6].map((n) => (
            <option key={n} value={n}>
              {n} {n > 1 ? t("widget.guests_plural") : t("widget.guest")}
            </option>
          ))}
        </select>
      </Field>
      <button
        data-testid="widget-search-btn"
        type="submit"
        className="md:col-span-3 bg-brand-primary hover:bg-brand-primaryHover text-white px-4 md:px-6 py-4 rounded-sm font-medium tracking-wide flex items-center justify-center gap-2 transition-colors duration-300 text-sm"
      >
        <Search size={16} /> {t("widget.search")}
      </button>
    </form>
  );
}

function Field({ label, icon, children, className = "" }) {
  return (
    <label className={`flex flex-col justify-center px-4 py-3 ${className}`}>
      <span className="text-[10px] uppercase tracking-[0.18em] text-brand-muted flex items-center gap-1.5 mb-1.5">
        {icon} {label}
      </span>
      {children}
    </label>
  );
}
